/**
 * React Query hooks for Sequence Definitions API
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import type {
  SequenceDefinition,
  SequenceDefinitionListItem,
  SequenceDefinitionCreate,
  SequenceDefinitionUpdate,
} from '@/types/sequence';
import {
  listSequenceDefinitions,
  getSequenceDefinition,
  createSequenceDefinition,
  updateSequenceDefinition,
  deleteSequenceDefinition,
} from '../sequences';

// Query keys
export const sequenceKeys = {
  all: ['sequences'] as const,
  definitions: () => [...sequenceKeys.all, 'definitions'] as const,
  definition: (id: string) => [...sequenceKeys.definitions(), id] as const,
};

/**
 * List all sequence definitions
 */
export function useSequenceDefinitions(): UseQueryResult<
  { definitions: SequenceDefinitionListItem[]; count: number },
  Error
> {
  return useQuery({
    queryKey: sequenceKeys.definitions(),
    queryFn: listSequenceDefinitions,
  });
}

/**
 * Get a single sequence definition by ID
 */
export function useSequenceDefinition(id: string): UseQueryResult<SequenceDefinition, Error> {
  return useQuery({
    queryKey: sequenceKeys.definition(id),
    queryFn: () => getSequenceDefinition(id),
    enabled: !!id,
  });
}

/**
 * Create a new sequence definition
 */
export function useCreateSequenceDefinition(): UseMutationResult<
  SequenceDefinition,
  Error,
  SequenceDefinitionCreate,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSequenceDefinition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sequenceKeys.definitions() });
    },
  });
}

/**
 * Update a sequence definition
 */
export function useUpdateSequenceDefinition(): UseMutationResult<
  SequenceDefinition,
  Error,
  { id: string; data: SequenceDefinitionUpdate },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => updateSequenceDefinition(id, data),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(sequenceKeys.definition(variables.id), data);
      queryClient.invalidateQueries({ queryKey: sequenceKeys.definitions() });
    },
  });
}

/**
 * Delete a sequence definition
 */
export function useDeleteSequenceDefinition(): UseMutationResult<void, Error, string, unknown> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSequenceDefinition,
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: sequenceKeys.definition(id) });
      queryClient.invalidateQueries({ queryKey: sequenceKeys.definitions() });
    },
  });
}
